import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SuperAdminSidebar from "../components/SuperAdminSidebar";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, ExternalLink, Settings, Search, Check, X, Clock, Copy } from "lucide-react";

export default function SuperAdminRestaurants() {
    const [list, setList] = useState([]);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("all"); // all | pending | approved | rejected
    const load = () => api.get("/superadmin/restaurants").then((r) => setList(r.data));
    useEffect(() => { load(); }, []);

    const counts = useMemo(() => {
        const c = { all: list.length, pending: 0, approved: 0, rejected: 0 };
        list.forEach((r) => {
            const s = r.approval_status || "approved";
            if (c[s] !== undefined) c[s] += 1;
        });
        return c;
    }, [list]);

    const filtered = useMemo(() => {
        let out = list;
        if (filter !== "all") {
            out = out.filter((r) => (r.approval_status || "approved") === filter);
        }
        if (query.trim()) {
            const q = query.toLowerCase().trim();
            out = out.filter((r) =>
                (r.name || "").toLowerCase().includes(q) ||
                (r.city || "").toLowerCase().includes(q) ||
                (r.slug || "").toLowerCase().includes(q) ||
                (r.owner_username || "").toLowerCase().includes(q) ||
                (r.owner_email || "").toLowerCase().includes(q) ||
                (r.vat_number || "").toLowerCase().includes(q),
            );
        }
        return out;
    }, [list, query, filter]);

    const toggleActive = async (r) => {
        try {
            await api.patch(`/superadmin/restaurants/${r.restaurant_id}`, { active: !r.active });
            toast.success(r.active ? "Ristorante disattivato" : "Ristorante attivato");
            load();
        } catch { toast.error("Errore"); }
    };

    const approve = async (r) => {
        try {
            await api.post(`/superadmin/restaurants/${r.restaurant_id}/approve`);
            toast.success(`Ristorante "${r.name}" approvato. Email inviata al ristoratore.`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore approvazione");
        }
    };

    const reject = async (r) => {
        if (!window.confirm(`Rifiutare l'iscrizione di "${r.name}"?`)) return;
        try {
            await api.post(`/superadmin/restaurants/${r.restaurant_id}/reject`);
            toast.success("Iscrizione rifiutata");
            load();
        } catch { toast.error("Errore"); }
    };

    return (
        <div className="flex min-h-screen bg-ink" data-testid="superadmin-restaurants-page">
            <SuperAdminSidebar />
            <main className="flex-1 p-10">
                <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <div className="text-gold text-xs tracking-[0.4em] uppercase">Gestione</div>
                        <h1 className="font-serif text-5xl text-white mt-2">Ristoranti</h1>
                        <p className="text-gray-400 text-sm mt-2">{filtered.length} di {list.length}</p>
                    </div>
                    <Link to="/adminrenova/restaurants/new" data-testid="add-restaurant-btn"
                        className="inline-flex items-center gap-2 bg-gold text-black font-semibold px-5 py-3 rounded-sm hover:bg-gold-light">
                        <Plus size={16} /> Nuovo
                    </Link>
                </div>

                {/* Status filter pills */}
                <div className="flex flex-wrap gap-2 mb-5" data-testid="status-filters">
                    {[
                        { k: "all",      label: "Tutti",         count: counts.all },
                        { k: "pending",  label: "In attesa",     count: counts.pending },
                        { k: "approved", label: "Approvati",     count: counts.approved },
                        { k: "rejected", label: "Rifiutati",     count: counts.rejected },
                    ].map((f) => (
                        <button key={f.k}
                            onClick={() => setFilter(f.k)}
                            data-testid={`filter-${f.k}`}
                            className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${
                                filter === f.k
                                    ? "bg-gold text-black border-gold"
                                    : "border-white/15 text-gray-400 hover:border-gold/40 hover:text-gold"
                            } ${f.k === "pending" && f.count > 0 && filter !== "pending" ? "ring-1 ring-amber-500/40 animate-pulse" : ""}`}>
                            {f.label} <span className="opacity-60 ml-1">({f.count})</span>
                        </button>
                    ))}
                </div>

                {/* Search */}
                <div className="mb-6">
                    <div className="relative max-w-xl">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gold" size={16} strokeWidth={1.5} />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Cerca per nome, città, slug, owner, email, P.IVA..."
                            data-testid="superadmin-search-input"
                            className="w-full pl-10 pr-10 py-3 bg-ink-surface border border-white/20 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none text-sm"
                        />
                        {query && (
                            <button
                                onClick={() => setQuery("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gold text-xs uppercase tracking-widest"
                                data-testid="superadmin-search-clear">
                                Pulisci
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid gap-4">
                    {filtered.length === 0 ? (
                        <div className="bg-ink-surface border border-white/10 rounded-sm p-12 text-center text-gray-500" data-testid="search-empty">
                            {list.length === 0 ? "Nessun ristorante ancora." : "Nessun risultato per i filtri attuali."}
                        </div>
                    ) : filtered.map((r) => {
                        const status = r.approval_status || "approved";
                        return (
                        <div key={r.restaurant_id} data-testid={`rest-card-${r.slug}`}
                            className={`bg-ink-surface border rounded-sm p-6 transition-colors ${status === "pending" ? "border-amber-500/40" : "border-white/10 hover:border-gold/30"}`}>
                            <div className="flex items-start justify-between flex-wrap gap-4">
                                <div className="flex-1 min-w-0 space-y-2">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <h3 className="font-serif text-2xl text-white">{r.name}</h3>
                                        {r.city && <span className="text-[10px] uppercase tracking-widest text-gold">{r.city}</span>}
                                        {status === "pending" && (
                                            <span className="text-[9px] uppercase tracking-widest bg-amber-500/15 text-amber-300 border border-amber-500/40 px-2 py-0.5 rounded-sm inline-flex items-center gap-1" data-testid={`badge-pending-${r.slug}`}>
                                                <Clock size={10} /> In attesa
                                            </span>
                                        )}
                                        {status === "approved" && (
                                            r.active
                                                ? <span className="text-[9px] uppercase tracking-widest bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-sm">Attivo</span>
                                                : <span className="text-[9px] uppercase tracking-widest bg-red-500/10 text-red-400 border border-red-500/30 px-2 py-0.5 rounded-sm">Disattivato</span>
                                        )}
                                        {status === "rejected" && (
                                            <span className="text-[9px] uppercase tracking-widest bg-rose-500/15 text-rose-300 border border-rose-500/40 px-2 py-0.5 rounded-sm">Rifiutato</span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                                        <span>URL: <code className="text-gold">/r/{r.slug}</code></span>
                                        {r.owner_username && <span>Owner: <code className="text-white">{r.owner_username}</code></span>}
                                        <span>Email: {r.owner_email}</span>
                                        {r.phone && <span>Tel: {r.phone}</span>}
                                        {r.vat_number && <span>P.IVA: <code className="text-white">{r.vat_number}</code></span>}
                                        {r.address && <span className="basis-full">📍 {r.address}</span>}
                                        <span>Prenotazioni: {r.reservations_count}</span>
                                    </div>
                                    {status === "pending" && r.description && (
                                        <p className="text-xs text-gray-400 italic mt-2 max-w-2xl">"{r.description}"</p>
                                    )}
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    {status === "pending" ? (
                                        <>
                                            <button onClick={() => approve(r)}
                                                data-testid={`approve-${r.slug}`}
                                                className="inline-flex items-center gap-1.5 text-xs bg-emerald-500 text-black font-semibold px-4 py-2 rounded-sm hover:bg-emerald-400 tracking-widest uppercase">
                                                <Check size={13} /> Approva
                                            </button>
                                            <button onClick={() => reject(r)}
                                                data-testid={`reject-${r.slug}`}
                                                className="inline-flex items-center gap-1.5 text-xs border border-rose-500/40 text-rose-300 px-4 py-2 rounded-sm hover:bg-rose-500/10 tracking-widest uppercase">
                                                <X size={13} /> Rifiuta
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <a href={`/r/${r.slug}`} target="_blank" rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gold px-3 py-2 border border-white/10 rounded-sm"
                                                data-testid={`visit-${r.slug}`}>
                                                <ExternalLink size={12} /> Visita
                                            </a>
                                            <button onClick={() => toggleActive(r)}
                                                className={`text-xs px-3 py-2 rounded-sm border ${r.active ? "border-amber-500/30 text-amber-400 hover:bg-amber-500/10" : "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"}`}
                                                data-testid={`toggle-${r.slug}`}>
                                                {r.active ? "Disattiva" : "Attiva"}
                                            </button>
                                            <Link to={`/adminrenova/restaurants/${r.restaurant_id}`}
                                                className="inline-flex items-center gap-1 text-xs text-gold px-3 py-2 border border-gold/40 rounded-sm hover:bg-gold/10"
                                                data-testid={`manage-${r.slug}`}>
                                                <Settings size={12} /> Gestisci
                                            </Link>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        );
                    })}
                </div>
            </main>
        </div>
    );
}

